import React, { useContext, useMemo, createContext, useEffect } from 'react'
import { NO_USER } from '../../Api/firebase/AuthData'
import { SnackbarContext } from '../SnackbarContext'
import { LinkContext } from '..'
import {
  getCurrentUrl,
  handleAuthError,
  handleFirebaseTransactionError,
  normalizeError,
  resetFirebasePersistence,
  useCookieStorage,
  configureFirebaseForDevelopment,
} from '../../Utils'
import {
  useAuthState,
  useAuthOperations,
  useGoogleAuth,
  useEmailAuth,
  useFirebaseAuthListener,
} from '../../Hooks/auth'
import { AuthDisplayContext } from './AuthDisplayContext'
import type { AuthContextType, AuthProviderProps } from './types'
import { isSignInWithEmailLink } from 'firebase/auth'
import { getFirAuth } from 'Api/firebase/firebaseConnect'
import { ANALYTICS_TAGS } from 'Constant'
import { useQuery } from 'Routing/utils'

export const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { getCookie, setCookie, removeCookie } = useCookieStorage()
  const { sendMasterBrazeEvents } = useContext(LinkContext)!
  const { toggleShowSnackbar, showFirebaseErrorSnackbar } = useContext(SnackbarContext)!
  const query = useQuery()

  useEffect(() => {
    initialize()
    void configureFirebaseForDevelopment()
  }, [])

  const initialize = async () => {
    try {
      await authOperations.waitForAuth()

      const auth = await getFirAuth()
      if (!auth) {
        throw new Error('Firebase auth not initialized after waiting.')
      }

      await authOperations.getAuthRedirectResult()

      const fbctQuery = query.get('fbct')
      if (fbctQuery) {
        await authOperations.handleCustomToken(fbctQuery)
      }

      if (isSignInWithEmailLink(auth, getCurrentUrl())) {
        await emailAuth.handleEmailLink()
      }

      googleAuth.initializeGSI()
    } catch (error) {
      const errorMessage = normalizeError(error)
      if (handleFirebaseTransactionError(errorMessage.message, 'auth_initialization')) {
        const refreshFn = await resetFirebasePersistence()
        showFirebaseErrorSnackbar(refreshFn)
        return
      }

      handleAuthError(
        ANALYTICS_TAGS.auth_initialization_error,
        error,
        'auth_initialization'
      )
      authStateSetters.setUser(NO_USER)
      authStateSetters.setAuthLoading(false)
    }
  }

  const authState = useAuthState()
  const {
    user,
    userFavorites,
    authLoading,
    showLogin,
    setUser,
    setAuthLoading,
    setShowLogin,
    setUserFavorites,
    resetAuthState,
    updateFavsInState,
    updateProfileInState,
    updateUserFieldInState,
    clearTeamFavorite,
    login,
    handleToggleLogin,
    getMissingUserFields,
  } = authState

  const authStateSetters = useMemo(
    () => ({
      setUser,
      setUserFavorites,
      setAuthLoading,
      setShowLogin,
      resetAuthState,
    }),
    [setUser, setUserFavorites, setAuthLoading, setShowLogin, resetAuthState]
  )

  const authOperations = useAuthOperations(toggleShowSnackbar, authStateSetters)
  const googleAuth = useGoogleAuth(toggleShowSnackbar, setShowLogin)
  const emailAuth = useEmailAuth(toggleShowSnackbar, getCookie, setCookie, removeCookie)

  useFirebaseAuthListener({
    authOperations,
    googleAuth,
    authStateSetters,
    sendMasterBrazeEvents,
    showFirebaseErrorSnackbar,
  })

  const contextValue: AuthContextType = useMemo(
    () => ({
      ...(user || NO_USER),
      ...userFavorites,
      authLoading,
      login,
      handleToggleLogin,
      logout: authOperations.logout,
      updateFavsInState,
      updateProfileInState,
      updateUserFieldInState,
      clearTeamFavorite,
      getAuthToken: authOperations.getAuthToken,
      getMissingUserFields,
      showLogin,
      signInWithGoogle: googleAuth.signInWithGooglePopup,
      signInWithEmLink: emailAuth.signInWithEmLink,
    }),
    [
      user,
      userFavorites,
      authLoading,
      login,
      handleToggleLogin,
      authOperations.logout,
      authOperations.getAuthToken,
      updateFavsInState,
      updateProfileInState,
      updateUserFieldInState,
      clearTeamFavorite,
      getMissingUserFields,
      showLogin,
      googleAuth.signInWithGooglePopup,
      emailAuth.signInWithEmLink,
    ]
  )

  const displayContextValue = useMemo(
    () => ({
      user: user ? { ...user, ...userFavorites } : null,
      authLoading,
      isLoggedIn: user?.isLoggedIn || false,
      isAnonymous: user?.isAnonymous || false,
      firstName: user?.firstName || null,
      email: user?.email || null,
    }),
    [user, userFavorites, authLoading]
  )

  return (
    <AuthContext.Provider value={contextValue}>
      <AuthDisplayContext.Provider value={displayContextValue}>
        {children}
      </AuthDisplayContext.Provider>
    </AuthContext.Provider>
  )
}

export const useAuthContext = (): AuthContextType => {
  try {
    const context = useContext(AuthContext)
    if (!context) {
      throw new Error(
        'useAuthContext must be used within an AuthProvider. Make sure your component is wrapped with AuthProvider.'
      )
    }
    return context
  } catch (error) {
    console.error('Error in useAuthContext:', error)
    console.error('Current AuthContext:', AuthContext)
    throw error
  }
}
